import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";
import { Link } from "react-router";
import { routePaths } from "../app/navigation";
import { useI18n } from "../shared/i18n/I18nContext";
import type { MessageKey } from "../shared/i18n/messages.de";
import { cx } from "../shared/ui/classNames";
import { ErrorMessage } from "../shared/ui/ErrorMessage";
import styles from "../shared/ui/ui.module.css";
import type { PushState } from "./pushNotifications";
import { usePushController } from "./PushContext";

/** Query key of the push state of this device. */
export const pushStateQueryKey = ["device", "push-state"] as const;

const stateMessages: Readonly<Record<PushState, MessageKey>> = {
  unsupported: "push.state.unsupported",
  installRequired: "push.state.installRequired",
  notConfigured: "push.state.notConfigured",
  denied: "push.state.denied",
  off: "push.state.off",
  on: "push.state.on",
};

/**
 * Opt-in for reminders as push notifications on this device (T-071). Nothing is sent without the
 * explicit choice of the person.
 *
 * @returns The settings section.
 */
export function NotificationSettings(): JSX.Element {
  const { t } = useI18n();
  const controller = usePushController();
  const queryClient = useQueryClient();
  const state = useQuery({
    queryKey: pushStateQueryKey,
    queryFn: () => controller.getState(),
    staleTime: 60_000,
  });
  const change = useMutation({
    mutationFn: (enable: boolean) => (enable ? controller.enable() : controller.disable()),
    onSuccess: (next) => {
      queryClient.setQueryData(pushStateQueryKey, next);
    },
  });
  const current = state.data;

  const section = (
    <section aria-labelledby="push-settings" className={cx(styles["card"], styles["stack"])}>
      <h2 id="push-settings">{t("push.title")}</h2>
      <p>{t("push.hint")}</p>
      {state.isError ? <ErrorMessage error={state.error} /> : null}
      {current === undefined ? null : <p role="status">{t(stateMessages[current])}</p>}
      {current === "installRequired" ? (
        <Link to={routePaths.install}>{t("nav.install")}</Link>
      ) : null}
      {current === "off" || current === "on" ? (
        <div className={styles["row"]}>
          <button
            type="button"
            className={cx(styles["button"], current === "off" ? styles["primary"] : undefined)}
            disabled={change.isPending}
            onClick={() => {
              change.mutate(current === "off");
            }}
          >
            {current === "off" ? t("push.enable") : t("push.disable")}
          </button>
        </div>
      ) : null}
      {change.isError ? <ErrorMessage error={change.error} /> : null}
    </section>
  );

  return section;
}
